import { NoSsr } from "@mui/base";
import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { useResponsive } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";

import { useIsScrollingOverAppBarThreshold } from "scmp-app/components/app-bar";
import { HideElementOnScrollEffectThreshold } from "scmp-app/components/app-bar/consts";
import { useAppBarContext } from "scmp-app/components/app-bar/contexts";
import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import type { HeaderConfiguration } from "scmp-app/components/header/types";
import { HomeEdition } from "scmp-app/components/home/edition";

import { DynamicHomeNewsTicker } from "./dynamics";
import {
  Container,
  Content,
  HeaderContainer,
  HomeHeaderContainer,
  LeftFeatureContainer,
  OnelineMenuContainer,
  RightFeatureContainer,
  StyledHeaderOnelineMenuContainer,
  StyledHomeMiniHeader,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
};

export const HomeHeader: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
  initEdition,
}) => {
  const { setShouldSwap } = useAppBarContext();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const threshold = isDesktopUp
    ? HideElementOnScrollEffectThreshold.desktop
    : HideElementOnScrollEffectThreshold.mobile;
  const shouldSwap = useIsScrollingOverAppBarThreshold(threshold);

  useEffect(() => {
    if (!setShouldSwap) return;
    setShouldSwap(shouldSwap ?? false);
  }, [setShouldSwap, shouldSwap]);

  const headerFeatureProvider = useHeaderFeaturesProvider();

  return (
    <>
      <Container className={className}>
        <HomeHeaderContainer ref={reference}>
          <HomeEdition />
          <HeaderContainer>
            <Wrapper>
              <NoSsr>
                <DynamicHomeNewsTicker />
              </NoSsr>
              <Content>
                <LeftFeatureContainer>
                  {headerFeatureProvider.hamburgerMenu({ showIn: "mobileAndTablet" })}
                  {headerFeatureProvider.search({ showIn: "desktopUp" })}
                  {headerFeatureProvider.newsletter({ showIn: "desktopUp" })}
                  {headerFeatureProvider.myNews({ showIn: "tabletUp" })}
                </LeftFeatureContainer>
                {headerFeatureProvider.logoTextLink({ showIn: "mobileUp", variant: "default" })}
                <RightFeatureContainer>
                  {headerFeatureProvider.subscription({ showIn: "tabletUp" })}
                  {headerFeatureProvider.dashboard({ isHomepage: true })}
                </RightFeatureContainer>
              </Content>
              <OnelineMenuContainer>
                <StyledHeaderOnelineMenuContainer initEdition={initEdition} />
              </OnelineMenuContainer>
            </Wrapper>
          </HeaderContainer>
        </HomeHeaderContainer>
      </Container>

      <StyledHomeMiniHeader
        $shouldSwap={shouldSwap}
        headerConfiguration={headerConfiguration}
        initEdition={initEdition}
        isHomeMiniHeader={true}
      />
    </>
  );
};

HomeHeader.displayName = "HomeHeader";
